
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Filter, X } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import type { Tables } from '@/integrations/supabase/types';

type Column = Tables<'columns'>;
type Card = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

export interface ColumnFilters {
  tags: string[];
  endDate: 'due_soon' | 'overdue' | 'no_date' | null;
  assignedTo: string | null;
  progressState: string | null;
  category: string | null;
  sortBy: 'position' | 'title' | 'end_date' | 'created_at' | 'progress_state' | null;
  sortOrder: 'asc' | 'desc';
}

interface ColumnFilterProps {
  column: Column;
  filters: ColumnFilters;
  onFiltersChange: (filters: ColumnFilters) => void;
  cards: Card[];
}

export const ColumnFilter = ({ column, filters, onFiltersChange, cards }: ColumnFilterProps) => {
  const { tagDefinitions, profiles } = useBoardContext();
  const [isOpen, setIsOpen] = useState(false);

  // Get unique values from cards for filter options
  const uniqueCategories = [...new Set(cards.map(card => card.category).filter(Boolean))];
  const assignedUsers = [...new Set(cards.map(card => card.assigned_to).filter(Boolean))];

  const handleTagFilter = (tagId: string) => {
    const newTags = filters.tags.includes(tagId)
      ? filters.tags.filter(id => id !== tagId)
      : [...filters.tags, tagId];
    onFiltersChange({ ...filters, tags: newTags });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      tags: [],
      endDate: null,
      assignedTo: null,
      progressState: null,
      category: null,
      sortBy: null,
      sortOrder: 'asc'
    });
  };

  const getActiveFilterCount = () => {
    return (
      filters.tags.length +
      (filters.endDate ? 1 : 0) +
      (filters.assignedTo ? 1 : 0) +
      (filters.progressState ? 1 : 0) +
      (filters.category ? 1 : 0) +
      (filters.sortBy ? 1 : 0)
    );
  };

  const getTagName = (tagId: string) => {
    return tagDefinitions?.find(tag => tag.id === tagId)?.name || 'Unknown';
  };

  const getUserName = (userId: string) => {
    return profiles?.find(profile => profile.id === userId)?.full_name || 'Unknown User';
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200 relative"
        >
          <Filter className="h-3 w-3" />
          {activeFilterCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Filter & Sort Cards</h4>
            {activeFilterCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
                className="h-6 text-xs"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Sort Options */}
          <div className="border-b pb-3">
            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Sort By</h5>
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={filters.sortBy || 'default_position'}
                onValueChange={(value) => 
                  onFiltersChange({ 
                    ...filters, 
                    sortBy: value === 'default_position' ? null : value as ColumnFilters['sortBy']
                  })
                }
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default_position">Default Order</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="end_date">Due Date</SelectItem>
                  <SelectItem value="created_at">Created Date</SelectItem>
                  <SelectItem value="progress_state">Progress</SelectItem>
                </SelectContent>
              </Select>
              
              <Select
                value={filters.sortOrder}
                onValueChange={(value) => 
                  onFiltersChange({ ...filters, sortOrder: value as 'asc' | 'desc' })
                }
                disabled={!filters.sortBy}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags Filter */}
          {tagDefinitions && tagDefinitions.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Tags</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {tagDefinitions.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={filters.tags.includes(tag.id) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    style={filters.tags.includes(tag.id) ? { backgroundColor: tag.color } : {}}
                    onClick={() => handleTagFilter(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* End Date Filter */}
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Due Date</label>
            <Select
              value={filters.endDate || 'all_dates'}
              onValueChange={(value) => 
                onFiltersChange({ ...filters, endDate: value === 'all_dates' ? null : value as ColumnFilters['endDate'] })
              }
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Filter by due date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_dates">All dates</SelectItem>
                <SelectItem value="due_soon">Due within 7 days</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="no_date">No due date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assigned User Filter */}
          {assignedUsers.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Assigned To</label>
              <Select
                value={filters.assignedTo || 'all_users'}
                onValueChange={(value) => 
                  onFiltersChange({ ...filters, assignedTo: value === 'all_users' ? null : value })
                }
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Filter by assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_users">All users</SelectItem>
                  {assignedUsers.map((userId) => (
                    <SelectItem key={userId} value={userId}>
                      {getUserName(userId)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Progress State Filter */}
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Progress</label>
            <Select
              value={filters.progressState || 'all_progress'}
              onValueChange={(value) => 
                onFiltersChange({ ...filters, progressState: value === 'all_progress' ? null : value })
              }
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Filter by progress" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_progress">All progress states</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          {uniqueCategories.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Category</label>
              <Select
                value={filters.category || 'all_categories'}
                onValueChange={(value) => 
                  onFiltersChange({ ...filters, category: value === 'all_categories' ? null : value })
                }
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_categories">All categories</SelectItem>
                  {uniqueCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
